/**
 * Servicio Unificado de Almacenamiento
 * Detecta automáticamente la plataforma y usa el servicio adecuado:
 * - Web: IndexedDB (localStorageService)
 * - iOS/Android: SQLite (sqliteService)
 */

import { getStorageType, StorageType, getPlatformInfo } from '../utils/platformDetector';
import localStorageService, { Tables } from './localStorageService';

/**
 * Servicio unificado de almacenamiento
 * Abstrae la lógica de detección de plataforma
 */
class UnifiedStorageService {
  constructor() {
    this.storageType = null;
    this.service = null;
    this.initialized = false;
  }

  /**
   * Inicializa el servicio detectando la plataforma
   */
  async init() {
    if (this.initialized) {
      return;
    }

    const platformInfo = getPlatformInfo();
    this.storageType = platformInfo.storageType;

    console.group('🚀 Inicializando Storage Service');
    console.log('Platform:', platformInfo.platform);
    console.log('Storage Type:', this.storageType);
    console.groupEnd();

    switch (this.storageType) {
      case StorageType.INDEXEDDB:
        // Usar IndexedDB para Web
        this.service = localStorageService;
        await this.service.init();
        break;

      case StorageType.SQLITE:
        // Usar SQLite para iOS/Android
        // NOTA: Cuando se implemente la app mobile, descomentar:
        /*
        const sqliteService = require('../../mobile/src/services/sqliteService').default;
        this.service = sqliteService;
        await this.service.init();
        */
        throw new Error('SQLite no está disponible aún. Esta funcionalidad se implementará en la app móvil.');

      case StorageType.MEMORY:
        // Fallback a almacenamiento en memoria (temporal)
        console.warn('⚠️ Usando almacenamiento en memoria (temporal). Los datos se perderán al recargar.');
        this.service = this.createMemoryStorage();
        break;

      default:
        throw new Error(`Tipo de almacenamiento no soportado: ${this.storageType}`);
    }

    this.initialized = true;
    console.log('✅ Storage Service inicializado correctamente');
  }

  /**
   * Crea un almacenamiento en memoria como fallback
   */
  createMemoryStorage() {
    const memoryStore = {};
    let nextId = 1;

    Object.values(Tables).forEach(tableName => {
      memoryStore[tableName] = [];
    });

    return {
      async add(tableName, data) {
        const id = nextId++;
        memoryStore[tableName].push({ ...data, id });
        return id;
      },
      async get(tableName, id) {
        return memoryStore[tableName].find(item => item.id === id);
      },
      async getAll(tableName) {
        return [...memoryStore[tableName]];
      },
      async update(tableName, id, data) {
        const index = memoryStore[tableName].findIndex(item => item.id === id);
        if (index !== -1) {
          memoryStore[tableName][index] = { ...data, id };
        }
        return id;
      },
      async delete(tableName, id) {
        memoryStore[tableName] = memoryStore[tableName].filter(item => item.id !== id);
      },
      async count(tableName) {
        return memoryStore[tableName].length;
      },
      async clear(tableName) {
        memoryStore[tableName] = [];
      }
    };
  }

  /**
   * Verifica que el servicio esté inicializado
   */
  ensureInitialized() {
    if (!this.initialized) {
      throw new Error('Storage Service no está inicializado. Llama a init() primero.');
    }
  }

  // ==================== MÉTODOS DELEGADOS ====================

  /**
   * Agrega un registro
   */
  async add(tableName, data) {
    this.ensureInitialized();
    return this.service.add(tableName, data);
  }

  /**
   * Obtiene un registro por ID
   */
  async get(tableName, id) {
    this.ensureInitialized();
    return this.service.get(tableName, id);
  }

  /**
   * Obtiene todos los registros
   */
  async getAll(tableName) {
    this.ensureInitialized();
    return this.service.getAll(tableName);
  }

  /**
   * Actualiza un registro
   */
  async update(tableName, id, data) {
    this.ensureInitialized();
    return this.service.update(tableName, id, data);
  }

  /**
   * Elimina un registro
   */
  async delete(tableName, id) {
    this.ensureInitialized();
    return this.service.delete(tableName, id);
  }

  /**
   * Cuenta los registros
   */
  async count(tableName) {
    this.ensureInitialized();
    return this.service.count(tableName);
  }

  /**
   * Limpia una tabla
   */
  async clear(tableName) {
    this.ensureInitialized();
    return this.service.clear(tableName);
  }

  // ==================== MÉTODOS ESPECÍFICOS POR TABLA ====================

  get eventos() {
    this.ensureInitialized();
    return this.service.eventos || {
      agregar: (data) => this.add(Tables.EVENTOS, data),
      obtenerTodos: () => this.getAll(Tables.EVENTOS),
      obtenerPorId: (id) => this.get(Tables.EVENTOS, id),
      actualizar: (id, data) => this.update(Tables.EVENTOS, id, data),
      eliminar: (id) => this.delete(Tables.EVENTOS, id)
    };
  }

  get eventCategories() {
    this.ensureInitialized();
    return this.service.eventCategories || {
      agregar: (data) => this.add(Tables.EVENT_CATEGORIES, data),
      obtenerTodas: () => this.getAll(Tables.EVENT_CATEGORIES),
      obtenerPorId: (id) => this.get(Tables.EVENT_CATEGORIES, id),
      actualizar: (id, data) => this.update(Tables.EVENT_CATEGORIES, id, data),
      eliminar: (id) => this.delete(Tables.EVENT_CATEGORIES, id)
    };
  }

  get posts() {
    this.ensureInitialized();
    return this.service.posts || {
      agregar: (data) => this.add(Tables.POSTS, data),
      obtenerTodos: () => this.getAll(Tables.POSTS),
      obtenerPorId: (id) => this.get(Tables.POSTS, id),
      actualizar: (id, data) => this.update(Tables.POSTS, id, data),
      eliminar: (id) => this.delete(Tables.POSTS, id)
    };
  }

  get historicalData() {
    this.ensureInitialized();
    return this.service.historicalData || {
      agregar: (data) => this.add(Tables.HISTORICAL_DATA, data),
      obtenerTodos: () => this.getAll(Tables.HISTORICAL_DATA),
      obtenerPorId: (id) => this.get(Tables.HISTORICAL_DATA, id),
      actualizar: (id, data) => this.update(Tables.HISTORICAL_DATA, id, data),
      eliminar: (id) => this.delete(Tables.HISTORICAL_DATA, id)
    };
  }

  get bibleNotes() {
    this.ensureInitialized();
    return this.service.bibleNotes || {
      agregar: (data) => this.add(Tables.BIBLE_NOTES, data),
      obtenerTodas: () => this.getAll(Tables.BIBLE_NOTES),
      obtenerPorId: (id) => this.get(Tables.BIBLE_NOTES, id),
      actualizar: (id, data) => this.update(Tables.BIBLE_NOTES, id, data),
      eliminar: (id) => this.delete(Tables.BIBLE_NOTES, id)
    };
  }

  get bibleHighlights() {
    this.ensureInitialized();
    return this.service.bibleHighlights || {
      agregar: (data) => this.add(Tables.BIBLE_HIGHLIGHTS, data),
      obtenerTodos: () => this.getAll(Tables.BIBLE_HIGHLIGHTS),
      obtenerPorId: (id) => this.get(Tables.BIBLE_HIGHLIGHTS, id),
      actualizar: (id, data) => this.update(Tables.BIBLE_HIGHLIGHTS, id, data),
      eliminar: (id) => this.delete(Tables.BIBLE_HIGHLIGHTS, id)
    };
  }

  get bibleRelations() {
    this.ensureInitialized();
    return this.service.bibleRelations || {
      agregar: (data) => this.add(Tables.BIBLE_RELATIONS, data),
      obtenerTodas: () => this.getAll(Tables.BIBLE_RELATIONS),
      obtenerPorId: (id) => this.get(Tables.BIBLE_RELATIONS, id),
      actualizar: (id, data) => this.update(Tables.BIBLE_RELATIONS, id, data),
      eliminar: (id) => this.delete(Tables.BIBLE_RELATIONS, id)
    };
  }

  // ==================== UTILIDADES ====================

  /**
   * Obtiene información del servicio
   */
  getInfo() {
    return {
      storageType: this.storageType,
      initialized: this.initialized,
      platformInfo: getPlatformInfo()
    };
  }

  /**
   * Obtiene estadísticas
   */
  async getStats() {
    this.ensureInitialized();
    if (this.service.getStats) {
      return this.service.getStats();
    }
    // Fallback manual
    const stats = {};
    for (const tableName of Object.values(Tables)) {
      stats[tableName] = await this.count(tableName);
    }
    return stats;
  }
}

// Exportar instancia única (Singleton)
const storageService = new UnifiedStorageService();

export default storageService;
export { Tables };

/**
 * EJEMPLO DE USO:
 * 
 * import storageService from './services/storageService';
 * 
 * // Inicializar (hacer una sola vez al inicio de la app)
 * await storageService.init();
 * 
 * // Usar el servicio
 * const id = await storageService.eventos.agregar({
 *   nombre: 'Mi Evento',
 *   fechaInicio: '2025-11-20',
 *   tipo: 'personal'
 * });
 * 
 * const eventos = await storageService.eventos.obtenerTodos();
 * 
 * await storageService.eventos.actualizar(id, {
 *   nombre: 'Mi Evento Actualizado'
 * });
 * 
 * await storageService.eventos.eliminar(id);
 */

